import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Clock, Send, AlertTriangle, Save, Lock } from "lucide-react";

export default function ExamRoom() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [answers, setAnswers] = useState({});       // {question_id: selected_index}
    const [essay, setEssay] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [remaining, setRemaining] = useState(0);
    const [currentIdx, setCurrentIdx] = useState(0);
    const submitRef = useRef(null);

    useEffect(() => {
        api.post(`/exam/start/${id}`)
            .then((r) => { setSession(r.data); setLoading(false); })
            .catch((e) => { setError(formatApiError(e.response?.data?.detail)); setLoading(false); });
    }, [id]);

    // Countdown
    useEffect(() => {
        if (!session) return;
        const expires = new Date(session.expires_at).getTime();
        const tick = () => {
            const r = Math.max(0, Math.floor((expires - Date.now()) / 1000));
            setRemaining(r);
            if (r === 0 && !submitting) {
                submitRef.current?.();
            }
        };
        tick();
        const intv = setInterval(tick, 1000);
        return () => clearInterval(intv);
    }, [session, submitting]);

    const onSubmit = useCallback(async () => {
        if (!session || submitting) return;
        setSubmitting(true); setError("");
        const payload = {
            session_id: session.session_id,
            answers: Object.entries(answers).map(([qid, idx]) => ({ question_id: qid, selected_index: idx })),
            essay_text: essay,
        };
        try {
            const { data } = await api.post("/exam/submit", payload);
            navigate(`/ket-qua/${data.id}`);
        } catch (e) {
            setError(formatApiError(e.response?.data?.detail));
            setSubmitting(false);
        }
    }, [answers, essay, session, submitting, navigate]);
    submitRef.current = onSubmit;

    if (loading) return <main className="pt-32 container-x text-center"><div className="text-[var(--gold-deep)]">Đang tải phiên thi…</div></main>;
    if (error && !session) return <main className="pt-32 container-x text-center">
        <h2 className="text-2xl font-heading mb-3">Không thể bắt đầu</h2>
        <p className="text-red-400">{error}</p>
        <button onClick={() => navigate(-1)} className="btn-secondary mt-4">Quay lại</button>
    </main>;

    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    const lowTime = remaining < 300;
    const currentQ = session.questions[currentIdx];

    return (
        <main className="pt-20 pb-12 min-h-screen relative">
            {/* Fixed top bar */}
            <div className="fixed top-16 left-0 right-0 z-40 backdrop-blur-xl bg-white/95 border-b border-[var(--border)]">
                <div className="container-x py-3 flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <div className="text-xs text-slate-500 uppercase tracking-widest">Đang thi</div>
                        <div className="font-heading font-semibold truncate" data-testid="exam-title">{session.contest.title}</div>
                    </div>
                    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl border ${lowTime ? "border-red-400 bg-red-400/10 text-red-400" : "border-[var(--gold)] bg-[var(--gold)]/10 text-[var(--gold-deep)]"}`} data-testid="exam-timer">
                        <Clock className="w-4 h-4" />
                        <span className="font-mono text-lg tabular-nums">
                            {String(minutes).padStart(2, "0")}:{String(seconds).padStart(2, "0")}
                        </span>
                    </div>
                </div>
            </div>

            <div className="container-x pt-28 grid lg:grid-cols-12 gap-6">
                {/* Sidebar: question grid */}
                <aside className="lg:col-span-3">
                    <div className="card p-5 sticky top-32">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Trắc nghiệm</div>
                        <div className="grid grid-cols-5 gap-2">
                            {session.questions.map((q, i) => {
                                const answered = answers[q.id] != null;
                                return (
                                    <button key={q.id} onClick={() => setCurrentIdx(i)}
                                        className={`aspect-square rounded-lg text-sm font-semibold border transition ${
                                            i === currentIdx ? "border-[var(--gold)] bg-[var(--gold)]/20 text-[var(--gold-deep)]"
                                                : answered ? "border-[var(--gold)]/40 bg-[var(--gold)]/5 text-[var(--gold-deep)]"
                                                    : "border-[var(--border)] text-slate-500 hover:border-[var(--border-strong)]"
                                        }`}
                                        data-testid={`q-nav-${i}`}>
                                        {i + 1}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="border-t border-[var(--border)] mt-4 pt-4 text-xs text-slate-500">
                            <div className="flex justify-between mb-1"><span>Đã trả lời</span><span className="text-[var(--gold-deep)]">{Object.keys(answers).length}/{session.questions.length}</span></div>
                            {session.essay_prompt && <div className="flex justify-between"><span>Tự luận</span><span className={essay.length > 50 ? "text-[var(--gold-deep)]" : "text-slate-500"}>{essay.length} ký tự</span></div>}
                        </div>
                        <div className="border-t border-[var(--border)] mt-4 pt-4">
                            <button onClick={onSubmit} disabled={submitting} className="btn-primary w-full" data-testid="submit-exam-btn">
                                <Send className="w-4 h-4" />
                                {submitting ? "Đang nộp…" : "Nộp bài"}
                            </button>
                            <div className="text-xs text-slate-500 mt-2 flex items-start gap-1">
                                <Lock className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                Bài sẽ bị khoá ngay khi nộp.
                            </div>
                        </div>
                    </div>
                </aside>

                {/* Main: question + essay */}
                <section className="lg:col-span-9 space-y-6">
                    {currentQ && (
                        <div className="card p-7" data-testid={`question-${currentIdx}`}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="text-xs text-slate-500 uppercase tracking-widest">Câu {currentIdx + 1} / {session.questions.length}</div>
                                {currentQ.topic && <span className="badge badge-slate">{currentQ.topic}</span>}
                            </div>
                            <h2 className="font-heading text-xl font-semibold leading-relaxed mb-6">{currentQ.question}</h2>
                            <div className="space-y-3">
                                {currentQ.options.map((opt, i) => {
                                    const isActive = answers[currentQ.id] === i;
                                    return (
                                        <label key={i} className={`block p-4 rounded-xl border cursor-pointer transition ${
                                            isActive ? "border-[var(--gold)] bg-[var(--gold)]/10" : "border-[var(--border)] hover:border-[var(--border-strong)] hover:bg-[var(--bg-alt)]"
                                        }`} data-testid={`option-${currentIdx}-${i}`}>
                                            <input type="radio" name={currentQ.id} value={i} className="hidden"
                                                checked={isActive} onChange={() => setAnswers({ ...answers, [currentQ.id]: i })} />
                                            <div className="flex items-start gap-3">
                                                <div className={`w-7 h-7 flex-shrink-0 rounded-full border-2 flex items-center justify-center text-sm font-mono ${
                                                    isActive ? "border-[var(--gold)] bg-cyan-400 text-[#0B0A1C]" : "border-[var(--border-strong)] text-slate-500"
                                                }`}>{String.fromCharCode(65 + i)}</div>
                                                <div className="text-slate-700">{opt}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="flex justify-between mt-6">
                                <button onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
                                    className="btn-secondary" disabled={currentIdx === 0} data-testid="prev-q">← Câu trước</button>
                                <button onClick={() => setCurrentIdx(Math.min(session.questions.length - 1, currentIdx + 1))}
                                    className="btn-secondary" disabled={currentIdx === session.questions.length - 1} data-testid="next-q">Câu tiếp →</button>
                            </div>
                        </div>
                    )}

                    {/* Essay */}
                    {session.essay_prompt && (
                        <div className="card p-7" data-testid="essay-section">
                            <div className="text-xs text-slate-500 uppercase tracking-widest mb-3">Phần tự luận</div>
                            <p className="text-slate-700 italic mb-4 leading-relaxed">"{session.essay_prompt}"</p>
                            <textarea value={essay} onChange={(e) => setEssay(e.target.value)} rows={12}
                                className="input resize-y" placeholder="Viết bài tự luận của bạn ở đây…" data-testid="essay-textarea" />
                            <div className="flex justify-between items-center mt-3 text-xs">
                                <span className="text-slate-500">{essay.trim().split(/\s+/).filter(Boolean).length} từ · {essay.length} ký tự</span>
                                <span className="text-slate-500 flex items-center gap-1"><Save className="w-3 h-3" /> Tự động lưu khi nộp</span>
                            </div>
                        </div>
                    )}

                    {error && <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/30 rounded-lg p-3 flex gap-2"><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}</div>}

                    <button onClick={onSubmit} disabled={submitting} className="btn-primary w-full" data-testid="submit-exam-btn-bottom">
                        <Send className="w-4 h-4" /> {submitting ? "Đang nộp…" : "Nộp bài và kết thúc"}
                    </button>
                </section>
            </div>
        </main>
    );
}
